/**
 * Templates Page - 워크플로우 템플릿
 *
 * 배포된 워크플로우들을 템플릿으로 보여주고, 불러오기/복사 기능 제공
 * - 카드: 공통 .item-card + .flow-card (styles/flowCard.css) - 헤더 / 플로우(FlowSteps) / 푸터 3영역, 챗봇 미리보기와 동일
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Topbar } from '../components/layout';
import { Badge, Button, useToast, SearchInput, Tabs } from '../components/ui';
import { workflowApi, authApi, Workflow } from '../api';
import { FlowSteps } from '../components/workflow';
import { isAdminRole } from '../utils/role';
import ChatIcon from '../assets/icons/marketplace/chat.svg?react';
import ScheduleIcon from '../assets/icons/marketplace/schedule.svg?react';
import MailIcon from '../assets/icons/marketplace/mail.svg?react';
import googleDriveLogo from '../assets/icons/marketplace/google-drive.svg';
import gmailLogo from '../assets/icons/marketplace/gmail.svg';
import slackLogo from '../assets/icons/marketplace/slack.svg';
import notionLogo from '../assets/icons/marketplace/notion.svg';
import naverLogo from '../assets/icons/marketplace/naver.svg';
import './TemplatesPage.css';

// 트리거 노드 판별 대상 (못 찾거나 'trigger' 면 챗봇)
const TRIGGER_TYPES = ['trigger', 'schedule-trigger', 'mail-trigger', 'webhook-trigger', 'file-trigger',
  'notion', 'google-drive', 'gmail', 'naver', 'slack'];

// 트리거 → 뱃지 문구 · 카드 아이콘 (아이콘 칸 색은 마켓플레이스와 동일: 트리거 = info, 커넥터 로고 = default)
type SvgIcon = React.FC<React.SVGProps<SVGSVGElement>>;
interface TriggerInfo {
  label: string;
  icon?: SvgIcon;    // SVG 컴포넌트
  logo?: string;     // 로고 이미지 경로
  emoji?: string;    // 전용 아이콘 없을 때
  iconClass: string;
}
const TRIGGER_INFO: Record<string, TriggerInfo> = {
  'chatbot':          { label: '챗봇',   icon: ChatIcon,        iconClass: 'item-card-icon--info' },
  'schedule-trigger': { label: '스케줄', icon: ScheduleIcon,    iconClass: 'item-card-icon--info' },
  'mail-trigger':     { label: '메일',   icon: MailIcon,        iconClass: 'item-card-icon--info' },
  'webhook-trigger':  { label: '웹훅',   emoji: '🔗',           iconClass: 'item-card-icon--info' },
  'file-trigger':     { label: '파일',   emoji: '📁',           iconClass: 'item-card-icon--info' },
  'notion':           { label: 'Notion', logo: notionLogo,      iconClass: '' },
  'google-drive':     { label: 'Drive',  logo: googleDriveLogo, iconClass: '' },
  'gmail':            { label: 'Gmail',  logo: gmailLogo,       iconClass: '' },
  'naver':            { label: '네이버', logo: naverLogo,       iconClass: '' },
  'slack':            { label: 'Slack',  logo: slackLogo,       iconClass: '' },
};

// 워크플로우 → 분류 키 (카드 뱃지 · 탭 공통). 트리거 노드 없거나 'trigger' 면 챗봇, 매핑 없으면 자동화
const getTriggerKey = (workflow: Workflow) => {
  const triggerNode = workflow.nodes?.find(n => TRIGGER_TYPES.includes(n.type));
  if (!triggerNode || triggerNode.type === 'trigger') return 'chatbot';
  return TRIGGER_INFO[triggerNode.type] ? triggerNode.type : 'automation';
};

const renderTriggerIcon = (info?: TriggerInfo) => {
  if (!info) return '⚡';
  if (info.icon) {
    const Icon = info.icon;
    return <Icon />;
  }
  if (info.logo) return <img src={info.logo} alt="" />;
  return info.emoji ?? '⚡';
};

export function TemplatesPage() {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const currentUser = authApi.getUser();
  const isAdmin = isAdminRole(currentUser?.role);
  const [workflows, setWorkflows] = useState<Workflow[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('all');

  useEffect(() => {
    loadWorkflows();
  }, []);

  const loadWorkflows = async () => {
    try {
      setLoading(true);
      const data = await workflowApi.list({ deployed: true });
      // isSharedAsTemplate: true 인 것만 표시
      const templates = data.filter((w) => w.isSharedAsTemplate === true);
      setWorkflows(templates);
    } catch (error) {
      showToast('error', '템플릿을 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleUseTemplate = (workflowId: string) => {
    navigate(`/workflows/${workflowId}`, { state: { fromTemplates: true } });
  };

  // 검색 필터
  const searchedWorkflows = workflows.filter((w) => {
    if (searchQuery && !w.name.toLowerCase().includes(searchQuery.toLowerCase())) {
      return false;
    }
    return true;
  });
  // 분류 탭 필터
  const filteredWorkflows = activeTab === 'all'
    ? searchedWorkflows
    : searchedWorkflows.filter((w) => getTriggerKey(w) === activeTab);

  // 분류 탭 - 전체 + 템플릿에 있는 분류만 (TRIGGER_INFO 순서, 자동화는 마지막). 숫자는 검색 결과 기준 (마켓플레이스와 동일)
  const tabKeys = [...Object.keys(TRIGGER_INFO), 'automation']
    .filter((key) => workflows.some((w) => getTriggerKey(w) === key));
  const tabItems = [
    { key: 'all', label: <>전체<span className="tab-count">{searchedWorkflows.length}</span></> },
    ...tabKeys.map((key) => ({
      key,
      label: (
        <>
          {TRIGGER_INFO[key]?.label ?? '자동화'}
          <span className="tab-count">{searchedWorkflows.filter((w) => getTriggerKey(w) === key).length}</span>
        </>
      ),
    })),
  ];

  return (
    <>
      <Topbar
        title="템플릿"
        description="배포된 워크플로우를 복사하여 새로운 워크플로우를 만들어보세요"
      />

      <div className="page-content">
        <div className="page-content-inner">
          {/* 분류 탭 (공통 Tabs) + 우측 검색 - 챗봇 미리보기와 동일 */}
          <div className="tabs-row">
            {!loading && workflows.length > 0 && (
              <Tabs items={tabItems} active={activeTab} onChange={setActiveTab} />
            )}
            <div className="tabs-row-aside">
              <SearchInput
                placeholder="템플릿 이름으로 검색"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onClear={() => setSearchQuery('')}
              />
            </div>
          </div>

          {/* 템플릿 그리드 */}
          {loading ? (
            <div className="loading">로딩 중...</div>
          ) : workflows.length > 0 && filteredWorkflows.length === 0 ? (
            <div className="empty-state">
              <p>조건에 맞는 템플릿이 없습니다.</p>
            </div>
          ) : filteredWorkflows.length === 0 ? (
            <div className="empty-state">
              <p>공유된 템플릿이 없습니다.</p>
              <p>워크플로우를 배포한 후 "템플릿으로 공유" 버튼을 눌러 등록하세요.</p>
              {isAdmin && (
                <Button onClick={() => navigate('/home')} className='mt-10'>워크플로우 만들기</Button>
              )}
            </div>
          ) : (
            <div className="card-grid flow-card-grid">
              {filteredWorkflows.map((workflow) => {
                const trigger = TRIGGER_INFO[getTriggerKey(workflow)];
                const createdAt = workflow.createdAt ? new Date(workflow.createdAt).toLocaleDateString() : '-';
                return (
                  <div key={workflow.id} className="item-card flow-card">
                    {/* 헤더: [트리거 텍스트] 이름 · 설명 */}
                    <div className="flow-card-head">
                      <div className="flow-card-info">
                        <div className="flow-card-title">
                          <Badge color="info">{trigger?.label ?? '자동화'}</Badge>
                          <span>{workflow.name}</span>
                        </div>
                        <p>{workflow.description || '설명이 없습니다.'}</p>
                      </div>
                    </div>

                    {/* 플로우: 회색 패널 안 세로 단계 */}
                    <FlowSteps workflow={workflow} />

                    {/* 푸터: 만든사람 · 생성일 + 버튼 */}
                    <div className="flow-card-foot">
                      <span className="flow-card-meta">
                        {workflow.ownerName && <span className="flow-card-owner">{workflow.ownerName}</span>}
                        <span>{createdAt}</span>
                      </span>
                      <div className="flow-card-actions">
                        <Button size="sm" color="gray" appearance="outline" onClick={() => handleUseTemplate(workflow.id)}>
                          상세보기
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default TemplatesPage;
